/**
 * Canonical assessment options and the economics behind them.
 *
 * Question sections AND the results calculator import from this module, so the
 * exact strings a user selects are the exact strings the math matches on.
 * Historically the questions and the calculator used different strings and
 * every score silently fell through to defaults — keep both sides on these
 * constants to prevent that class of bug.
 */

// ---------------------------------------------------------------------------
// Process section
// ---------------------------------------------------------------------------

export interface AutomatableShare {
  /** Lower bound of the share of this work that automation typically removes. */
  conservative: number;
  /** Realistic share for a well-executed implementation. */
  likely: number;
}

export interface ProcessProfile {
  automatableShare: AutomatableShare;
  recommendation: {
    title: string;
    description: string;
    effort: "Low" | "Medium" | "High";
    timeline: string;
  };
}

/**
 * The manual-process catalogue. Shares are deliberately bounded well below
 * 100% — these feed savings estimates shown to users, so conservative beats
 * impressive.
 */
export const MANUAL_PROCESS_PROFILES: Record<string, ProcessProfile> = {
  "Data entry & transfer between systems": {
    automatableShare: { conservative: 0.5, likely: 0.75 },
    recommendation: {
      title: "Automate data entry and system-to-system transfer",
      description:
        "Connect your systems so data entered once flows everywhere it is needed, using integration tools (e.g. Make, Zapier, n8n) or native integrations between your existing software.",
      effort: "Low",
      timeline: "2-4 weeks",
    },
  },
  "Invoicing & billing": {
    automatableShare: { conservative: 0.45, likely: 0.7 },
    recommendation: {
      title: "Automate invoicing and payment follow-up",
      description:
        "Generate invoices from your CRM or project data automatically and let payment reminders send themselves, so billing stops consuming staff time.",
      effort: "Low",
      timeline: "2-4 weeks",
    },
  },
  "Reporting & dashboards": {
    automatableShare: { conservative: 0.4, likely: 0.65 },
    recommendation: {
      title: "Replace manual reports with live dashboards",
      description:
        "Pipe your operational data into an always-current dashboard so weekly and monthly reports assemble themselves instead of being rebuilt by hand.",
      effort: "Medium",
      timeline: "3-6 weeks",
    },
  },
  "Customer/client onboarding": {
    automatableShare: { conservative: 0.35, likely: 0.55 },
    recommendation: {
      title: "Build an automated client onboarding workflow",
      description:
        "Turn onboarding into a templated sequence — welcome emails, document collection, account setup and kickoff scheduling triggered automatically when a deal closes.",
      effort: "Medium",
      timeline: "3-6 weeks",
    },
  },
  "Scheduling & calendar management": {
    automatableShare: { conservative: 0.45, likely: 0.7 },
    recommendation: {
      title: "Automate scheduling and calendar coordination",
      description:
        "Replace back-and-forth scheduling with self-serve booking links and automated reminders, including routing rules for different meeting types.",
      effort: "Low",
      timeline: "1-2 weeks",
    },
  },
  "Email triage & follow-ups": {
    automatableShare: { conservative: 0.25, likely: 0.45 },
    recommendation: {
      title: "Automate email triage and follow-up sequences",
      description:
        "Use shared-inbox rules, templates and AI-assisted drafting to handle routine email, and automated sequences so follow-ups never depend on someone remembering.",
      effort: "Medium",
      timeline: "2-4 weeks",
    },
  },
  "Document creation & handling": {
    automatableShare: { conservative: 0.35, likely: 0.55 },
    recommendation: {
      title: "Template and auto-generate recurring documents",
      description:
        "Generate proposals, contracts and recurring documents from templates filled with your existing data, with e-signature built into the flow.",
      effort: "Low",
      timeline: "2-3 weeks",
    },
  },
  "Approvals & sign-offs": {
    automatableShare: { conservative: 0.3, likely: 0.5 },
    recommendation: {
      title: "Move approvals into a structured workflow",
      description:
        "Replace approval-by-email with a workflow that routes requests to the right person, escalates automatically, and keeps an audit trail.",
      effort: "Medium",
      timeline: "3-5 weeks",
    },
  },
  "Lead follow-up & CRM updates": {
    automatableShare: { conservative: 0.4, likely: 0.6 },
    recommendation: {
      title: "Automate lead follow-up and CRM hygiene",
      description:
        "Log activity to your CRM automatically and trigger follow-up sequences the moment a lead comes in, so nothing slips while your team focuses on warm conversations.",
      effort: "Medium",
      timeline: "2-4 weeks",
    },
  },
  "Hiring & HR administration": {
    automatableShare: { conservative: 0.3, likely: 0.5 },
    recommendation: {
      title: "Streamline hiring and HR admin workflows",
      description:
        "Automate candidate communication, interview scheduling and new-hire paperwork so HR time goes to people decisions, not coordination.",
      effort: "Medium",
      timeline: "3-6 weeks",
    },
  },
};

export const MANUAL_PROCESS_OPTIONS = Object.keys(MANUAL_PROCESS_PROFILES);

export const HOURLY_COST_OPTIONS = [
  "Under $25/hour",
  "$25-$50/hour",
  "$50-$75/hour",
  "$75-$100/hour",
  "Over $100/hour",
  "Not sure",
] as const;

/** Midpoints used in savings math. 'Not sure' gets a deliberately low default. */
export const HOURLY_COST_MIDPOINTS: Record<string, number> = {
  "Under $25/hour": 20,
  "$25-$50/hour": 37,
  "$50-$75/hour": 62,
  "$75-$100/hour": 87,
  "Over $100/hour": 110,
  "Not sure": 35,
};

export const DEFAULT_HOURLY_COST = 35;

export const ERROR_IMPACT_OPTIONS = [
  "Rarely - almost never causes problems",
  "Occasionally - a few times a month",
  "Regularly - every week",
  "Constantly - it is a daily problem",
] as const;

/** Contribution of error frequency to the opportunity score (0-10). */
export const ERROR_IMPACT_SCORES: Record<string, number> = {
  "Rarely - almost never causes problems": 0,
  "Occasionally - a few times a month": 4,
  "Regularly - every week": 7,
  "Constantly - it is a daily problem": 10,
};

// ---------------------------------------------------------------------------
// Technology section
// ---------------------------------------------------------------------------

export const CURRENT_SYSTEMS_OPTIONS = [
  "Business Management Software (ERP)",
  "Customer Management Software (CRM)",
  "Project/Task Management Tools",
  "File/Document Storage Systems",
  "Team Communication Tools",
  "Custom-Built Software",
  "Older Software Systems",
  "Mostly Spreadsheets",
];

export const INTEGRATION_NEEDS_OPTIONS = [
  "Automatic Data Sharing Between Systems",
  "Connecting Different Software Together",
  "Automating Manual Steps",
  "Connecting to External Services",
  "Custom Software Connections",
];

export const AUTOMATION_LEVEL_OPTIONS = [
  "Manual Processes (No Automation)",
  "Simple Automation (Basic Templates, etc.)",
  "Some Automation (Some Tasks Automated)",
  "Heavily Automated (Most Tasks Automated)",
] as const;

/** How much room the current automation level leaves (0-25). */
export const AUTOMATION_GAP_SCORES: Record<string, number> = {
  "Manual Processes (No Automation)": 25,
  "Simple Automation (Basic Templates, etc.)": 18,
  "Some Automation (Some Tasks Automated)": 10,
  "Heavily Automated (Most Tasks Automated)": 2,
};

export const TECH_CHALLENGES_OPTIONS = [
  "Getting Systems to Work Together",
  "Data Accuracy & Reliability",
  "Outdated Systems & Code",
  "Systems Struggling as Business Grows",
  "Security & Privacy Concerns",
  "Speed & Performance Issues",
  "Team Struggling to Use New Tools",
];

// ---------------------------------------------------------------------------
// Team section
// ---------------------------------------------------------------------------

export const TEAM_SIZE_OPTIONS = [
  "1-10 people",
  "11-50 people",
  "51-200 people",
  "201-500 people",
  "501+ people",
] as const;

export const CHANGE_READINESS_OPTIONS = [
  "Resistant - Team prefers current ways of working",
  "Neutral - Team is neither excited nor resistant to change",
  "Open - Team is willing to try new approaches",
  "Eager - Team actively seeks out better ways to work",
] as const;

export const CHANGE_READINESS_SCORES: Record<string, number> = {
  "Resistant - Team prefers current ways of working": 25,
  "Neutral - Team is neither excited nor resistant to change": 50,
  "Open - Team is willing to try new approaches": 75,
  "Eager - Team actively seeks out better ways to work": 95,
};

// ---------------------------------------------------------------------------
// Goals section (internal step id remains 'social-media' for compatibility)
// ---------------------------------------------------------------------------

export const PRIORITY_OPTIONS = [
  "Growing revenue & sales",
  "Strategy & long-term planning",
  "Improving our product or service",
  "Customer relationships",
  "Hiring & developing the team",
  "Personal time & work-life balance",
];

export const OUTCOME_OPTIONS = [
  "Cut operating costs",
  "Free up my own time",
  "Scale without adding headcount",
  "Reduce errors & rework",
  "Respond to customers faster",
  "Better visibility into the business",
];

export const TIMELINE_OPTIONS = [
  "As soon as possible",
  "Within 1-3 months",
  "Within 3-6 months",
  "Just exploring for now",
] as const;

export const TIMELINE_SCORES: Record<string, number> = {
  "As soon as possible": 95,
  "Within 1-3 months": 80,
  "Within 3-6 months": 60,
  "Just exploring for now": 30,
};

export const BUDGET_OPTIONS = [
  "Under $500/month",
  "$500-$2,000/month",
  "$2,000-$5,000/month",
  "Over $5,000/month",
  "Not sure yet",
];

// ---------------------------------------------------------------------------
// Savings model constants
// ---------------------------------------------------------------------------

/** Working weeks per year used in projections — under 52 to absorb holidays and ramp-up. */
export const WORKING_WEEKS_PER_YEAR = 46;
